import {
  calculators,
  entities,
  enums,
  formatters,
  isStatusAfterStatus,
  isStatusBeforeStatus,
  selectors,
} from "@fraction/shared";
import _ from "lodash";
import { CalculatorIcon, Loader } from "lucide-react";
import { useCallback } from "react";
import { ReactNode, useMemo, useRef } from "react";
import { Tooltip } from "src/components";
import { AppStatusBadge } from "src/components/AppStatusBadge";
import { KeyValue } from "src/components/KeyValue";
import { ModifiableKeyMultiSelectEnumChoices } from "src/components/ModifiableKeyMultiSelectEnumChoices";
import { ModifiableKeySelectEnumChoices } from "src/components/ModifiableKeySelectEnumChoices";
import { ModifiableKeyValue, ModifiableKeyValueHandle } from "src/components/ModifiableKeyValue";
import { ModifiableTextArea } from "src/components/ModifiableTextArea";
import { Button } from "src/components/ui/button";
import { useAccountViewPreference } from "src/hooks/useAccountViewPreference";
import { useApplication } from "src/hooks/useApplication";
import { useMutation } from "src/lib";
import { cn } from "src/utilities/shadcnUtils";
import { ApplicationValuationKeyValue } from "./ApplicationValuationKeyValue";
import {
  EstimatedCloseDateModifiableKeyValue,
  RequestedCloseDateModifiableKeyValue,
} from "./CloseDateModifiableKeyValue";

const PRODUCTS_BY_LENDER: Partial<Record<enums.LenderName, enums.ProductType[]>> = {
  [enums.LenderName.FRACTION]: [enums.ProductType.NO_PAYMENTS_MORTGAGE],
  [enums.LenderName.SEQUENCE_CAPITAL]: [
    enums.ProductType.HOLDBACK_MORTGAGE,
    enums.ProductType.INTEREST_ONLY_MORTGAGE,
    enums.ProductType.AMORTIZING_MORTGAGE,
  ],
};

const getProductOptions = (lenderId?: enums.LenderName) => {
  if (!lenderId) {
    return [enums.ProductType.NO_PAYMENTS_MORTGAGE];
  }
  return PRODUCTS_BY_LENDER[lenderId] || [enums.ProductType.NO_PAYMENTS_MORTGAGE];
};

const ProductKeyValue = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  const options = getProductOptions(app?.lenderId);
  const {
    ref: holdbackPaymentAmountRef,
    isPending: holdbackPaymentAmountIsPending,
    calculate: calculateHoldbackPaymentAmount,
    calculated: holdbackPaymentAmountCalculated,
  } = useCalculateAndUpdate({
    app,
    calculate: calculators.application.calculateFullHoldbackAmount,
    formatValue: (val) => formatters.number.getCurrencyFromNumber(val),
    path: "holdbackPaymentAmount",
  });

  const {
    ref: holdbackAmountRef,
    isPending: holdbackAmountIsPending,
    calculate: calculateHoldbackAmount,
    calculated: holdbackAmountCalculated,
  } = useCalculateAndUpdate({
    app,
    calculate: calculators.application.calculateHoldbackAmount,
    formatValue: (val) => formatters.number.getCurrencyFromNumber(val),
    path: "holdbackAmount",
  });

  const onChangeHoldbackType = useCallback((app: entities.ApplicationT) => {
    const holdbackPaymentAmount = calculators.application.calculateHoldbackPaymentAmount(app);
    const holdbackAmount = calculators.application.calculateHoldbackAmount(app);
    holdbackPaymentAmountRef.current?.setValue(
      formatters.number.getCurrencyFromNumber(holdbackPaymentAmount)
    );
    holdbackAmountRef.current?.setValue(formatters.number.getCurrencyFromNumber(holdbackAmount));

    return {
      holdbackPaymentAmount,
      holdbackAmount,
    };
  }, []);

  const onChangeHoldbackPaymentAmount = useCallback((app: entities.ApplicationT) => {
    const holdbackAmount = (app?.holdbackPaymentAmount || 0) * (app?.termLengthMonths || 0);
    holdbackAmountRef.current?.setValue(formatters.number.getCurrencyFromNumber(holdbackAmount));

    return {
      holdbackAmount,
    };
  }, []);

  if (options.length === 1 && app?.type === options?.[0]) {
    return (
      <>
        <KeyValue
          label="Product type"
          loading={loading}
          value={formatters.enums.PRODUCT_TYPE_MAP[app?.type || options?.[0]]}
        />
        {app?.type === enums.ProductType.HOLDBACK_MORTGAGE ? (
          <>
            <KeyValue label="Holdback type" loading={loading} value={app?.holdbackType} />
            {!app?.holdbackType || app?.holdbackType === enums.HoldbackType.FULL_PAYMENT ? null : (
              <KeyValue
                label="Holdback payment amount"
                loading={loading}
                value={app?.holdbackPaymentAmount}
              />
            )}
            <KeyValue label="Holdback amount" loading={loading} value={app?.holdbackAmount} />
          </>
        ) : null}
      </>
    );
  }

  return (
    <>
      <ModifiableKeySelectEnumChoices
        label="Product type"
        options={options}
        formatLabel={formatters.enums.PRODUCT_TYPE_MAP}
        path="type"
        loading={loading}
        appId={app?.id}
      />
      {app?.type === enums.ProductType.HOLDBACK_MORTGAGE ? (
        <>
          <ModifiableKeySelectEnumChoices
            label="Holdback type"
            options={Object.values(enums.HoldbackType)}
            path="holdbackType"
            loading={loading}
            updateOtherApplicationValuesOnUpdate={onChangeHoldbackType}
            appId={app?.id}
            tooltip={
              <div className="text-xs">
                <p className="mb-1 font-semibold text-sm">
                  There are three different holdback types available to the borrower:
                </p>
                <ul>
                  <li>
                    <b>Full payment</b>: The full payment amount is paid each month. The borrower makes no
                    payments during the life of the loan.
                  </li>
                  <li>
                    <b>Partial payment</b>: The borrower makes some portion of the payment amount each month
                    for the life of the loan.
                  </li>
                  <li>
                    <b>Delayed payment</b>: The borrower makes no payments until the holdback amount is
                    exhausted. At that point, the full payment amount is paid each month.
                  </li>
                </ul>
              </div>
            }
          />

          {!app?.holdbackType ||
          [enums.HoldbackType.FULL_PAYMENT, enums.HoldbackType.PARTIAL_PAYMENT].includes(
            app?.holdbackType
          ) ? (
            <KeyValue
              label="Holdback amount"
              loading={loading}
              value={formatters.number.getCurrencyFromNumber(app?.holdbackAmount || 0)}
            />
          ) : (
            <ModifiableKeyValue
              ref={holdbackAmountRef}
              label="Holdback amount"
              path="holdbackAmount"
              tooltip="The total amount of money that will be held back from proceeds to provide monthly payments on behalf of the borrower."
              loading={loading}
              appId={app?.id}
              type="currency"
              sublabel={
                app?.holdbackType !== enums.HoldbackType.DELAYED_PAYMENT ? undefined : (
                  <p className="text-[10px]">
                    Will last{" "}
                    {Math.round(calculators.application.calculateHowLongDelayedHoldbackWillLast(app))} months
                  </p>
                )
              }
              cents
              rightContent={
                <CalculatorButton
                  tooltip={
                    <div className="text-sm">
                      Set the holdback amount to{" "}
                      <b>{formatters.number.getCurrencyFromNumber(holdbackAmountCalculated)}</b>.
                      <br />
                      <p className="text-xs">
                        This amount assumes that{" "}
                        {!app?.holdbackType ||
                        [enums.HoldbackType.FULL_PAYMENT, enums.HoldbackType.DELAYED_PAYMENT].includes(
                          app?.holdbackType
                        )
                          ? "the entire"
                          : "part of the"}{" "}
                        monthly payment is paid each month{" "}
                        {!app?.holdbackType ||
                        [enums.HoldbackType.FULL_PAYMENT, enums.HoldbackType.PARTIAL_PAYMENT].includes(
                          app?.holdbackType
                        )
                          ? "through the life of the loan"
                          : "until the holdback is exhausted, at which point full interest payments will be required"}
                        .
                      </p>
                    </div>
                  }
                  isPending={holdbackAmountIsPending}
                  onClick={calculateHoldbackAmount}
                />
              }
            />
          )}
          {!app?.holdbackType ||
          [enums.HoldbackType.FULL_PAYMENT, enums.HoldbackType.DELAYED_PAYMENT].includes(
            app?.holdbackType
          ) ? (
            <KeyValue
              label="Holdback payment amount"
              loading={loading}
              value={formatters.number.getCurrencyFromNumber(app?.holdbackPaymentAmount || 0)}
            />
          ) : (
            <ModifiableKeyValue
              ref={holdbackPaymentAmountRef}
              label="Holdback payment"
              path="holdbackPaymentAmount"
              tooltip="The amount of money from the holdback amount that will go towards the monthly payment."
              loading={loading}
              appId={app?.id}
              type="currency"
              updateOtherApplicationValuesOnUpdate={onChangeHoldbackPaymentAmount}
              value={app?.holdbackPaymentAmount || app?.paymentAmount || undefined}
              cents
              rightContent={
                <CalculatorButton
                  tooltip={
                    <p className="text-sm">
                      Set the holdback payment amount to{" "}
                      <b>{formatters.number.getCurrencyFromNumber(holdbackPaymentAmountCalculated)}</b>, which
                      is{" "}
                      {app?.holdbackType === enums.HoldbackType.PARTIAL_PAYMENT ? "half of" : "the same as"}{" "}
                      the payment amount of{" "}
                      <b>{formatters.number.getCurrencyFromNumber(app?.paymentAmount || 0)}</b>.
                    </p>
                  }
                  isPending={holdbackPaymentAmountIsPending}
                  onClick={calculateHoldbackPaymentAmount}
                />
              }
            />
          )}
        </>
      ) : null}
    </>
  );
};

const PrepaymentPrivilegeKeyValue = ({
  app,
  loading,
}: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { preference } = useAccountViewPreference();

  if (
    !["employee", "lender"].includes(preference) &&
    app?.status &&
    isStatusAfterStatus(app?.status, enums.ApplicationStatus.RECEIVING_DOCS)
  ) {
    return (
      <KeyValue label="Prepayment privilege" loading={loading} value={_.startCase(app?.earlyPayoutType)} />
    );
  }

  return (
    <ModifiableKeySelectEnumChoices
      label="Prepayment privilege"
      options={Object.values(enums.EarlyPayoutType)}
      path="earlyPayoutType"
      loading={loading}
      appId={app?.id}
    />
  );
};

const CompoundFrequencyKeyValue = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { preference } = useAccountViewPreference();

  if (!["employee", "lender"].includes(preference)) {
    return (
      <KeyValue label="Compound frequency" loading={loading} value={_.startCase(app?.compoundFrequency)} />
    );
  }

  return (
    <ModifiableKeySelectEnumChoices
      label="Compound frequency"
      options={Object.values(enums.CompoundFrequency)}
      path="compoundFrequency"
      loading={loading}
      appId={app?.id}
    />
  );
};

const DeclineReasonKeyValue = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { preference } = useAccountViewPreference();

  if (!["employee", "lender"].includes(preference)) {
    if (
      app?.status &&
      ![enums.ApplicationStatus.PENDING_DECLINE, enums.ApplicationStatus.REJECTED].includes(app?.status)
    ) {
      return null;
    }

    return (
      <KeyValue
        label="Decline reason(s)"
        loading={loading}
        value={app?.declineReason || "Reason not provided."}
      />
    );
  }

  return (
    <ModifiableTextArea
      label="Decline reason(s)"
      path="declineReason"
      loading={loading}
      appId={app?.id}
      value={app?.declineReason || undefined}
    />
  );
};

const LenderKeyValue = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { preference } = useAccountViewPreference();

  if (["conveyancer", "lender"].includes(preference)) {
    return <KeyValue label="Lender" loading={loading} value={_.startCase(app?.lenderId)} />;
  }

  if (["employee"].includes(preference)) {
    return (
      <ModifiableKeySelectEnumChoices
        label="Lender"
        loading={loading}
        options={Object.values(enums.LenderName)}
        path="lenderId"
        defaultValue={app?.lenderId}
        appId={app?.id}
      />
    );
  }

  return null;
};

const MaxLTVKeyValue = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { preference } = useAccountViewPreference();

  if (["employee", "lender"].includes(preference)) {
    return (
      <ModifiableKeyValue
        label="Max LTV"
        loading={loading}
        appId={app?.id}
        path="maximumLTV"
        type="percentage"
        formatValue={(val) => formatters.number.getPercentageFromDecimal(val, { noSymbol: true })}
        percentage
        tooltip="The maximum loan-to-value ratio Fraction is willing to offer for this property for the selected term length."
      />
    );
  }

  if (!loading && !app?.maximumLTV) {
    return null;
  }

  return (
    <KeyValue
      label="Max LTV"
      loading={loading}
      tooltip="The maximum loan-to-value ratio Fraction is willing to offer for this property for the selected term length."
      value={formatters.number.getPercentageFromDecimal(app?.maximumLTV)}
    />
  );
};

const InterestSection = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { preference } = useAccountViewPreference();

  if (!["employee", "lender"].includes(preference)) {
    return (
      <>
        <KeyValue
          label="Interest rate"
          loading={loading}
          value={`${formatters.number.getPercentageFromDecimal(app?.minimumRate)} ${
            app?.rateType ? _.startCase(app?.rateType).toLowerCase() : ""
          }`.trim()}
        />
        {app?.variableInterestRatePrimeName && app?.variableInterestRatePrimeSpread ? (
          <KeyValue
            label="Variable rate basis"
            loading={loading}
            value={`+${formatters.number.getPercentageFromDecimal(
              app.variableInterestRatePrimeSpread
            )} over ${app.variableInterestRatePrimeName}`}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <ModifiableKeyValue
        label="Interest rate"
        loading={loading}
        appId={app?.id}
        path="minimumRate"
        formatValue={(val) => formatters.number.getPercentageFromDecimal(val, { noSymbol: true })}
        type="percentage"
        percentage
      />
      <ModifiableKeySelectEnumChoices
        label="Rate type"
        options={Object.values(enums.RateType)}
        path="rateType"
        loading={loading}
        appId={app?.id}
      />
      <ModifiableKeySelectEnumChoices
        label="Compounding type"
        options={Object.values(enums.InterestType)}
        path="interestType"
        loading={loading}
        appId={app?.id}
      />
      {app?.rateType && [enums.RateType.VARIABLE, enums.RateType.CAPPED_VARIABLE].includes(app?.rateType) ? (
        <>
          <ModifiableKeyValue
            label="Variable Prime Spread"
            loading={loading}
            appId={app?.id}
            path="variableInterestRatePrimeSpread"
            type="percentage"
            formatValue={(val) => formatters.number.getPercentageFromDecimal(val, { noSymbol: true })}
            percentage
            tooltip="The spread over the prime rate for the variable interest rate."
          />
          <ModifiableKeyValue
            label="Variable Prime Name"
            loading={loading}
            appId={app?.id}
            path="variableInterestRatePrimeName"
            type="text"
          />
        </>
      ) : null}
    </>
  );
};

const CalculatorButton = ({
  tooltip,
  onClick,
  isPending,
}: { tooltip?: ReactNode; onClick: (arg: any) => any; isPending?: boolean }) => {
  return (
    <Tooltip white text={tooltip}>
      <Button
        disabled={isPending}
        size="sm"
        className="p-0.5 h-7 w-7 rounded-full"
        variant="ghost"
        onClick={onClick}
      >
        {isPending ? <Loader height={16} className="animate-spin" /> : <CalculatorIcon height={16} />}
      </Button>
    </Tooltip>
  );
};

const useCalculateAndUpdate = ({
  app,
  calculate,
  formatValue,
  path,
}: {
  app?: entities.ApplicationT;
  calculate: (app: entities.ApplicationT) => string | number;
  formatValue?: (value: string | number) => string;
  path: string;
}) => {
  const { updateApplication } = useApplication({ id: app?.id, fetchEnabled: false });
  const ref = useRef<ModifiableKeyValueHandle>(null);

  const handleCalculate = useMutation({
    mutationFn: async (e: any) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();

      if (!app) {
        return;
      }

      const result = calculate(app);
      ref.current?.setValue(formatValue ? formatValue(result) : result.toString());
      await updateApplication({ [path]: result });
      return result;
    },
  });

  const calculated = useMemo(() => (app ? calculate(app) : undefined), [calculate, app]);

  return {
    calculate: handleCalculate.mutateAsync,
    isPending: handleCalculate.isPending,
    ref,
    calculated,
  };
};

const calculatePaymentAmount = (app?: entities.ApplicationT) => {
  const loanAmount = selectors.application.selectApplicationAmount(app);
  const rate = app?.minimumRate;
  const paymentFrequency = app?.paymentFrequency || enums.Frequency.MONTHLY;
  const convertedRate = calculators.rates.convertAnnualToNominalRate(rate || 0, paymentFrequency);
  const paymentAmount = Math.round(convertedRate.mul(loanAmount).toNumber());
  return paymentAmount;
};

const PaymentAmountKeyValue = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { preference } = useAccountViewPreference();
  const { ref, calculate, isPending, calculated } = useCalculateAndUpdate({
    app,
    calculate: calculatePaymentAmount,
    path: "paymentAmount",
    formatValue: (val) => formatters.number.getCurrencyFromNumber(Number(val)),
  });

  const loanAmount = selectors.application.selectApplicationAmount(app);
  const rate = app?.minimumRate;
  const paymentFrequency = app?.paymentFrequency || enums.Frequency.MONTHLY;

  if (
    app?.type &&
    ![
      enums.ProductType.INTEREST_ONLY_MORTGAGE,
      enums.ProductType.HOLDBACK_MORTGAGE,
      enums.ProductType.AMORTIZING_MORTGAGE,
    ].includes(app?.type)
  ) {
    return null;
  }

  if (!["employee", "lender"].includes(preference)) {
    if (app?.paymentAmount && app?.paymentFrequency) {
      return (
        <KeyValue
          label="Payment amount"
          loading={loading}
          value={`${formatters.number.getCurrencyFromNumber(app?.paymentAmount)} ${
            formatters.enums.FREQUENCY_MAP[app?.paymentFrequency]
          }`.toLowerCase()}
        />
      );
    }

    return null;
  }

  return (
    <>
      <ModifiableKeySelectEnumChoices
        label="Payment frequency"
        options={[
          enums.Frequency.MONTHLY,
          enums.Frequency.BIWEEKLY,
          enums.Frequency.WEEKLY,
          enums.Frequency.SEMI_ANNUAL,
        ]}
        formatLabel={formatters.enums.FREQUENCY_MAP}
        path="paymentFrequency"
        loading={loading}
        appId={app?.id}
      />
      <ModifiableKeyValue
        ref={ref}
        label="Payment amount"
        loading={loading}
        appId={app?.id}
        path="paymentAmount"
        value={app?.paymentAmount || undefined}
        type="currency"
        cents
        rightContent={
          <CalculatorButton
            isPending={isPending}
            onClick={calculate}
            tooltip={
              rate && loanAmount ? (
                <div>
                  <p className="text-xs">
                    Set the payment amount to <b>{formatters.number.getCurrencyFromNumber(calculated)}</b>
                  </p>
                  <div className="text-[10px] bg-gray-200 rounded p-2 mt-1">
                    <p>
                      Formula is: <b>Loan Amount × (Annual Rate ÷ Periods per year)</b>
                      <br />
                      For this loan:{" "}
                      <b>
                        {formatters.number.getCurrencyFromNumber(loanAmount)} × (
                        {formatters.number.getPercentageFromDecimal(rate)} ÷{" "}
                        {calculators.rates.periodsInFrequency[paymentFrequency]}) ={" "}
                        {formatters.number.getCurrencyFromNumber(calculated)}
                      </b>
                    </p>
                    <p className="mt-1">
                      This type of rate calculation is called <i>nominal rate conversion</i>.
                    </p>
                  </div>
                </div>
              ) : null
            }
          />
        }
      />
    </>
  );
};

const AmountValue = ({ value, valuation }: { value: number; valuation?: number }) => {
  return (
    <p>
      {formatters.number.getCurrencyFromNumber(value)}
      {valuation ? (
        <span className="text-[8px] font-medium bg-gray-400 rounded-full p-1 px-2 ml-1 align-middle whitespace-nowrap">
          {formatters.number.getPercentageFromDecimal(value / valuation)} LTV
        </span>
      ) : (
        ""
      )}
    </p>
  );
};

export const ApplicationOverview = ({
  loading,
  app,
  className,
  header,
  showHeader = true,
}: {
  loading?: boolean;
  app?: entities.ApplicationT;
  className?: string;
  header?: string;
  showHeader?: boolean;
}) => {
  const { preference } = useAccountViewPreference();

  const canModifyEstimatedCloseDate =
    preference === "employee" ||
    preference === "lender" ||
    (preference === "conveyancer" &&
      app?.status &&
      isStatusBeforeStatus(app?.status, enums.ApplicationStatus.READY_FOR_CAPITAL_CALL, true));

  return (
    <div
      className={cn(
        "border p-6 border-gray-400 rounded max-w-full md:max-w-3xl overflow-x-scroll",
        className
      )}
    >
      {showHeader ? (
        <div>
          <div className="flex flex-row flex-wrap gap-2 mb-3 justify-between items-center">
            <p className="text-2xl font-bold">{header ? header : "Application overview"}</p>
            <AppStatusBadge appId={app?.id} className="float-right" loading={loading} status={app?.status} />
          </div>
          <p className="text-md mb-3">
            This is an overview of your loan application details. If anything looks incorrect, please let us
            know.
          </p>
        </div>
      ) : null}

      {preference === "employee" ? (
        <div className="flex flex-row gap-2">
          <a
            className="text-sm font-bold w-full"
            target="_blank"
            href={`https://app.hubspot.com/contacts/5572674/record/0-3/${app?.crmId}`}
          >
            <Button
              variant="green"
              size="sm"
              className="mb-2 mt-1 bg-[#FF7A59] hover:bg-[#FF7A59] hover:brightness-110 w-full"
            >
              Go to Hubspot
            </Button>
          </a>
          <a
            className="text-sm font-bold w-full"
            target="_blank"
            href={`https://fractionhq.retool.com/apps/85253540-3694-11eb-b225-777f1da022f5/Applications%20%26%20Applicants/Application%20Management#eid=${app?.eid}`}
          >
            <Button variant="blue" size="sm" className="mb-2 mt-1 w-full">
              Go to Retool
            </Button>
          </a>
        </div>
      ) : null}
      <div className="flex flex-col [&>*:nth-child(odd)]:bg-gray-200 [&>*:nth-child(even)]:white hover:[&>*]:bg-gray-300">
        <KeyValue label="Application ID" clipboard loading={loading} value={app?.eid} />
        <DeclineReasonKeyValue app={app} loading={loading} />
        <LenderKeyValue app={app} loading={loading} />
        <KeyValue
          label="Property address"
          loading={loading}
          value={formatters.property.formattedAddress(app?.property)}
        />
        <ProductKeyValue app={app} loading={loading} />
        <PaymentAmountKeyValue app={app} loading={loading} />
        <InterestSection app={app} loading={loading} />
        <LoanAmountSection app={app} loading={loading} />

        <PrepaymentPrivilegeKeyValue app={app} loading={loading} />
        <CompoundFrequencyKeyValue app={app} loading={loading} />

        <ModifiableKeySelectEnumChoices
          label="Term length"
          options={["12", "24", "36", "48", "60"]}
          path="termLengthMonths"
          formatLabel={(val) => `${Math.round(Number(val) / 12)} year${val === "12" ? "" : "s"}`}
          mutateValueBeforeUpdate={(val) => Number(val)}
          formatValue={(val) => val.toString()}
          loading={loading}
          appId={app?.id}
        />
        <ModifiableKeySelectEnumChoices
          label="Charge position"
          options={Object.values(enums.MortgagePosition)}
          path="position"
          defaultValue={enums.MortgagePosition.FIRST}
          loading={loading}
          appId={app?.id}
        />
        <ApplicationValuationKeyValue
          appId={app?.id}
          label="Appraised value"
          tooltip="The appraised value of the property from a third-party appraisal company."
          type={enums.PropertyValuationType.APPRAISAL}
          loading={loading}
        />
        <ApplicationValuationKeyValue
          appId={app?.id}
          label="Estimated home value"
          tooltip="The estimated home value (you can give your best guess)."
          type={enums.PropertyValuationType.HOMEOWNER_ESTIMATED}
          loading={loading}
        />
        <ModifiableKeySelectEnumChoices
          label="Property type"
          options={Object.values(enums.PropertyType)}
          formatLabel={formatters.enums.PROPERTY_TYPE_MAP}
          path="property.type"
          loading={loading}
          appId={app?.id}
        />
        <ModifiableKeySelectEnumChoices
          label="Property usage"
          options={Object.values(enums.PropertyUsage)}
          formatLabel={formatters.enums.PROPERTY_USAGE_MAP}
          path="property.usage"
          loading={loading}
          appId={app?.id}
        />

        <ModifiableKeyValue
          appId={app?.id}
          label="Solicitor name"
          tooltip="The name of the client's solicitor/lawyer for their ILR/ILA."
          inputClassName="w-full"
          loading={loading}
          path="solicitor.name"
          type="text"
        />
        <ModifiableKeyValue
          appId={app?.id}
          inputClassName="w-full"
          tooltip="The law firm of the client's solicitor."
          label="Solicitor firm"
          loading={loading}
          path="solicitor.lawFirmName"
          type="text"
        />
        <ModifiableKeyValue
          appId={app?.id}
          label="Solicitor email"
          tooltip="The email our conveyancer can contact the solicitor at."
          inputClassName="w-full"
          loading={loading}
          path="solicitor.email"
          type="email"
        />
        <ModifiableKeyValue
          appId={app?.id}
          label="Solicitor phone"
          tooltip="The phone number our conveyancer can contact the solicitor at."
          inputClassName="w-full"
          loading={loading}
          path="solicitor.phone"
          type="tel"
        />
        <ModifiableKeyMultiSelectEnumChoices
          label="Use of funds"
          options={Object.values(enums.UseOfFunds)}
          formatLabel={formatters.enums.USE_OF_FUNDS_MAP}
          path="useOfFundsChoices"
          loading={loading}
          appId={app?.id}
          defaultValues={app?.useOfFundsChoices}
        />
        <ModifiableTextArea
          label="Use of funds (other)"
          loading={loading}
          appId={app?.id}
          path="useOfFundsDescription"
          value={app?.useOfFundsDescription}
        />
        <MaxLTVKeyValue app={app} loading={loading} />

        {app?.appraisalPaymentToAppraiserDate ? (
          <KeyValue
            label="Appraisal ordered"
            loading={loading}
            value={formatters.date.formatDateNoYearAndDistance(app.appraisalPaymentToAppraiserDate, {
              addSuffix: true,
            })}
          />
        ) : null}
        {app?.appraisalAppointmentDate ? (
          <KeyValue
            label="Appraisal appointment"
            loading={loading}
            value={formatters.date.formatDateNoYearAndDistance(app.appraisalAppointmentDate, {
              addSuffix: true,
            })}
          />
        ) : null}

        {preference !== "conveyancer" ? (
          <RequestedCloseDateModifiableKeyValue app={app} loading={loading} />
        ) : null}

        <ModifiableTextArea
          label="Exit plan notes"
          loading={loading}
          appId={app?.id}
          path="exitPlanNotes"
          value={app?.exitPlanNotes}
          tooltip="A brief description of the exit plan for the loan by the end of term, i.e. how the Fraction mortgage will be paid out."
        />

        {app?.estimatedCloseDate && canModifyEstimatedCloseDate ? (
          <EstimatedCloseDateModifiableKeyValue app={app} loading={loading} />
        ) : null}
        {["employee", "conveyancer", "lender"].includes(preference) ? (
          <ModifiableKeyValue
            appId={app?.id}
            label="Signing appointment date"
            loading={loading}
            path="targetILRAppointmentDate"
            type="date"
            formatValue={formatters.date.iso8601}
          />
        ) : app?.targetILRAppointmentDate ? (
          <KeyValue
            label="Signing appointment date"
            loading={loading}
            value={formatters.date.formatDate(app.targetILRAppointmentDate)}
          />
        ) : null}
        {app?.estimatedCloseDate && !canModifyEstimatedCloseDate ? (
          <KeyValue
            label="Estimated close date"
            loading={loading}
            value={formatters.date.formatDate(app.estimatedCloseDate)}
          />
        ) : null}
        {app?.advanceDate && ["employee", "lender"].includes(preference) ? (
          <ModifiableKeyValue
            appId={app?.id}
            label="Advance date"
            loading={loading}
            path="advanceDate"
            type="date"
            formatValue={formatters.date.iso8601}
          />
        ) : null}
        {app?.advanceDate && !["employee", "lender"].includes(preference) ? (
          <KeyValue
            label="Advance date"
            loading={loading}
            value={formatters.date.formatDate(app.advanceDate)}
          />
        ) : null}

        {app?.closeDate ? (
          <KeyValue
            label="Official close date"
            loading={loading}
            value={formatters.date.formatDate(app.closeDate)}
          />
        ) : null}
        {_.sortBy(app?.applicants, "priority")?.map((applicant, index) => (
          <KeyValue
            key={applicant.id}
            label={`Applicant #${index + 1}`}
            loading={loading}
            value={`${formatters.user.userName(applicant.user)} ${
              applicant.user?.email ? `(${applicant.user.email})` : ""
            }`}
          />
        ))}
        {["employee", "lender"].includes(preference) ? (
          <KeyValue label="Conveyancer" loading={loading} value={app?.conveyancerGroup?.name} />
        ) : null}
        {["conveyancer", "employee", "lender"].includes(preference) ? (
          <ModifiableTextArea
            label="Conveyancer notes"
            loading={loading}
            appId={app?.id}
            path="conveyancerNotes"
            value={app?.conveyancerNotes}
          />
        ) : null}
        {["employee"].includes(preference) ? (
          <ModifiableTextArea
            label="Notes"
            loading={loading}
            appId={app?.id}
            path="notes"
            value={app?.notes}
          />
        ) : null}
        {["lender", "employee"].includes(preference) ? (
          <ModifiableTextArea
            label="Notes for partner"
            loading={loading}
            appId={app?.id}
            path="notesForWhitelabelPartner"
            value={app?.notes}
          />
        ) : null}
        {["employee"].includes(preference) ? (
          <ModifiableTextArea
            label="Internal notes"
            loading={loading}
            appId={app?.id}
            path="internalNotes"
            value={app?.internalNotes}
          />
        ) : null}
      </div>
    </div>
  );
};

const LoanAmountSection = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  const originationFee = selectors.application.selectApplicationFee(app);
  const { preference } = useAccountViewPreference();

  return (
    <>
      <KeyValue
        label="Total loan amount"
        loading={loading}
        tooltip="The total amount of the loan, including the origination fee and holdback amount (if applicable)."
        value={formatters.number.getCurrencyFromNumber(
          selectors.application.selectApplicationTotalAmount(app)
        )}
      />
      <KeyValue
        label="Net loan amount"
        loading={loading}
        tooltip={
          <p className="text-xs">
            The loan amount net origination fees (if fees are deducted from loan amount) and the holdback
            amount (if applicable).
            <br />
            <i>
              This does not account for other fees that may be deducted from the proceeds such as conveyancer
              fees or lawyer fees.
            </i>
          </p>
        }
        value={formatters.number.getCurrencyFromNumber(selectors.application.selectApplicationNetAmount(app))}
      />
      <KeyValue
        label={`${["employee", "lender", "broker"].includes(preference) ? "Total o" : "O"}rigination fee`}
        loading={loading}
        tooltip="The origination fee is a one-time fee charged by Fraction to cover the costs of processing the loan."
        value={
          originationFee.percentage ? (
            <p>
              {formatters.number.getCurrencyFromNumber(originationFee.total)}{" "}
              <span className="text-[8px] font-medium bg-gray-400 rounded-full py-1 px-2 align-middle truncate">
                {formatters.number.getPercentageFromDecimal(originationFee.percentage)} of loan
              </span>
            </p>
          ) : (
            "Not yet set"
          )
        }
      />
      <LoanAmountKeyValues app={app} loading={loading} />
    </>
  );
};

const LoanAmountKeyValues = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { preference } = useAccountViewPreference();

  if (preference === "employee") {
    return <EmployeeLoanAmountKeyValues app={app} loading={loading} />;
  } else if (preference === "conveyancer") {
    return <ConveyancerLoanAmountKeyValues app={app} loading={loading} />;
  } else if (preference === "lender") {
    return <LenderLoanAmountKeyValues app={app} loading={loading} />;
  } else if (preference === "broker" || preference === "applicant") {
    return <BrokerApplicantLoanAmountKeyValues app={app} loading={loading} />;
  }

  return null;
};

const BpsBadge = ({ app, feeInCents }: { app?: entities.ApplicationT; feeInCents?: number | null }) => {
  return (
    <p className="text-[10px] bg-gray-400 rounded-full w-fit px-1.5 font-semibold py-0.5 mt-0.5">
      {formatters.number.convertDecimalToBps(
        (feeInCents ?? 0) / selectors.application.selectApplicationAmount(app)
      )}{" "}
      bps
    </p>
  );
};

const calculateWhitelabelLenderFeeBps = (app?: entities.ApplicationT) => {
  if (
    app?.lenderId &&
    [enums.LenderName.FRACTION, enums.LenderName.SEQUENCE_CAPITAL].includes(app?.lenderId)
  ) {
    return 0;
  }
  return app?.product?.originationFeeToLenderInBps ?? 100;
};

const calculateWhitelabelLenderFee = (app?: entities.ApplicationT) => {
  const loanAmount = selectors.application.selectApplicationAmount(app);
  const bps = calculateWhitelabelLenderFeeBps(app);
  return Math.round(loanAmount * (bps / 10000));
};

const LenderOriginationFeeKeyValue = ({
  app,
  loading,
}: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { ref, calculate, isPending, calculated } = useCalculateAndUpdate({
    app,
    calculate: calculateWhitelabelLenderFee,
    path: "whitelabelLenderOriginationFeeInCents",
    formatValue: (val) => formatters.number.getCurrencyFromNumber(Number(val)),
  });

  return (
    <ModifiableKeyValue
      ref={ref}
      label="Lender origination fee"
      loading={loading}
      appId={app?.id}
      path="whitelabelLenderOriginationFeeInCents"
      sublabel={<BpsBadge app={app} feeInCents={app?.whitelabelLenderOriginationFeeInCents} />}
      value={app?.whitelabelLenderOriginationFeeInCents || undefined}
      type="currency"
      cents
      rightContent={
        <CalculatorButton
          isPending={isPending}
          onClick={calculate}
          tooltip={
            calculated !== undefined ? (
              <p className="text-xs">
                Set the fee to{" "}
                <b>
                  {formatters.number.getCurrencyFromNumber(calculated)} (
                  {calculateWhitelabelLenderFeeBps(app)} bps)
                </b>
              </p>
            ) : null
          }
        />
      }
    />
  );
};

const calculateFractionOriginationFeeBps = (app?: entities.ApplicationT) => {
  const defaultBps = app?.lenderId === enums.LenderName.FRACTION ? 200 : 100;
  return app?.product?.originationFeeToFractionInBps ?? defaultBps;
};
const calculateFractionOriginationFee = (app?: entities.ApplicationT) => {
  const loanAmount = selectors.application.selectApplicationAmount(app);
  const bps = calculateFractionOriginationFeeBps(app);
  return Math.round(loanAmount * (bps / 10000));
};

const FractionOriginationFeeKeyValue = ({
  app,
  loading,
}: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { ref, calculate, isPending, calculated } = useCalculateAndUpdate({
    app,
    calculate: calculateFractionOriginationFee,
    path: "originationFeeInCents",
    formatValue: (val) => formatters.number.getCurrencyFromNumber(Number(val)),
  });

  return (
    <ModifiableKeyValue
      ref={ref}
      label="Fraction origination fee"
      sublabel={<BpsBadge app={app} feeInCents={app?.originationFeeInCents} />}
      loading={loading}
      appId={app?.id}
      path="originationFeeInCents"
      value={app?.originationFeeInCents || undefined}
      type="currency"
      cents
      rightContent={
        <CalculatorButton
          isPending={isPending}
          onClick={calculate}
          tooltip={
            calculated !== undefined ? (
              <p className="text-xs">
                Set the fee to{" "}
                <b>
                  {formatters.number.getCurrencyFromNumber(calculated)} (
                  {calculateFractionOriginationFeeBps(app)} bps)
                </b>
              </p>
            ) : null
          }
        />
      }
    />
  );
};

const calculateBrokerOriginationFeeBps = (app?: entities.ApplicationT) => {
  return app?.brokerOriginationFeeInBPS ?? app?.product?.originationFeeToBrokerInBps ?? 100;
};

const calculateBrokerOriginationFee = (app?: entities.ApplicationT) => {
  const loanAmount = selectors.application.selectApplicationAmount(app);
  const bps = calculateBrokerOriginationFeeBps(app);
  return Math.round(loanAmount * (bps / 10000));
};

const BrokerOriginationFeeKeyValue = ({
  app,
  loading,
}: { app?: entities.ApplicationT; loading?: boolean }) => {
  const { ref, calculate, isPending, calculated } = useCalculateAndUpdate({
    app,
    calculate: calculateBrokerOriginationFee,
    path: "brokerOriginationFeeInCents",
    formatValue: (val) => formatters.number.getCurrencyFromNumber(Number(val)),
  });

  if (!loading && !app?.brokers?.length) {
    return null;
  }

  return (
    <ModifiableKeyValue
      ref={ref}
      label="Broker origination fee"
      sublabel={<BpsBadge app={app} feeInCents={app?.brokerOriginationFeeInCents} />}
      loading={loading}
      appId={app?.id}
      path="brokerOriginationFeeInCents"
      value={app?.brokerOriginationFeeInCents || undefined}
      type="currency"
      cents
      rightContent={
        <CalculatorButton
          isPending={isPending}
          onClick={calculate}
          tooltip={
            calculated ? (
              <p className="text-xs">
                Set the fee to{" "}
                <b>
                  {formatters.number.getCurrencyFromNumber(calculated)} (
                  {calculateBrokerOriginationFeeBps(app)} bps)
                </b>
              </p>
            ) : null
          }
        />
      }
    />
  );
};

const EmployeeLoanAmountKeyValues = ({
  app,
  loading,
}: { app?: entities.ApplicationT; loading?: boolean }) => {
  return (
    <>
      <ModifiableKeyValue
        label="Requested loan amount"
        loading={loading}
        appId={app?.id}
        path="requestedAmount"
        value={app?.requestedAmount || undefined}
        type="currency"
        cents
      />
      <ModifiableKeyValue
        label="Approved loan amount"
        loading={loading}
        appId={app?.id}
        path="approvedAmount"
        value={app?.approvedAmount || undefined}
        type="currency"
        cents
      />
      <ModifiableKeyValue
        label="Confirmed loan amount"
        loading={loading}
        appId={app?.id}
        path="confirmedLoanAmount"
        value={app?.confirmedLoanAmount || undefined}
        type="currency"
        cents
      />
      <FractionOriginationFeeKeyValue app={app} loading={loading} />
      {app?.lenderId && app?.lenderId !== enums.LenderName.FRACTION ? (
        <LenderOriginationFeeKeyValue app={app} loading={loading} />
      ) : null}
      <BrokerOriginationFeeKeyValue app={app} loading={loading} />
      {app?.lenderId && app?.lenderId !== enums.LenderName.FRACTION ? (
        <ModifiableKeyValue
          label="Lender application fee"
          loading={loading}
          appId={app?.id}
          path="lenderApplicationFeeInCents"
          value={app?.lenderApplicationFeeInCents || undefined}
          type="currency"
          cents
        />
      ) : null}
    </>
  );
};

const ConveyancerLoanAmountKeyValues = ({
  app,
  loading,
}: { app?: entities.ApplicationT; loading?: boolean }) => {
  return <EmployeeLoanAmountKeyValues app={app} loading={loading} />;
};

const LenderLoanAmountKeyValues = ({ app, loading }: { app?: entities.ApplicationT; loading?: boolean }) => {
  return (
    <>
      <ModifiableKeyValue
        label="Requested loan amount"
        loading={loading}
        appId={app?.id}
        path="requestedAmount"
        value={app?.requestedAmount || undefined}
        type="currency"
        cents
      />
      <ModifiableKeyValue
        label="Approved loan amount"
        loading={loading}
        appId={app?.id}
        path={["approvedAmount", "confirmedLoanAmount"]}
        value={app?.approvedAmount || undefined}
        type="currency"
        cents
      />
      <FractionOriginationFeeKeyValue app={app} loading={loading} />
      <LenderOriginationFeeKeyValue app={app} loading={loading} />
      <BrokerOriginationFeeKeyValue app={app} loading={loading} />
      <ModifiableKeyValue
        label="Lender application fee"
        loading={loading}
        appId={app?.id}
        path="lenderApplicationFeeInCents"
        value={app?.lenderApplicationFeeInCents || undefined}
        type="currency"
        cents
      />
    </>
  );
};

const BrokerApplicantLoanAmountKeyValues = ({
  app,
  loading,
}: { app?: entities.ApplicationT; loading?: boolean }) => {
  const valuation = app ? selectors.application.selectBestValuation(app)?.value : undefined;

  return (
    <>
      <ModifiableKeyValue
        label="Requested loan amount"
        loading={loading}
        appId={app?.id}
        path="requestedAmount"
        value={app?.requestedAmount || undefined}
        type="currency"
        cents
      />
      {app?.confirmedLoanAmount ? (
        <KeyValue
          label="Confirmed loan amount"
          loading={loading}
          value={<AmountValue valuation={valuation} value={app?.confirmedLoanAmount} />}
        />
      ) : app?.approvedAmount ? (
        <KeyValue
          label="Approved loan amount"
          loading={loading}
          value={<AmountValue valuation={valuation} value={app?.approvedAmount} />}
        />
      ) : null}
      <BrokerOriginationFeeKeyValue app={app} loading={loading} />
      <KeyValue
        label="Lender origination fee"
        loading={loading}
        value={formatters.number.getCurrencyFromNumber(
          (app?.originationFeeInCents || 0) + (app?.whitelabelLenderOriginationFeeInCents || 0)
        )}
      />
    </>
  );
};
